import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FileText, DollarSign, Users, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { apiService } from '../../services/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalShops: 0,
    availableShops: 0,
    pendingApplications: 0,
    monthlyRevenue: 0,
    occupancyRate: 0,
    totalTenants: 0
  });

  const [recentApplications, setRecentApplications] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [tenantPaymentStats, setTenantPaymentStats] = useState({
    totalCollected: 0,
    collectedThisMonth: 0,
    paymentsCount: 0,
    lastPaymentAt: null as string | null
  });
  const [recentTenantPayments, setRecentTenantPayments] = useState<any[]>([]);
  const [topTenantsByPaid, setTopTenantsByPaid] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load dashboard stats
      const [statsResponse, applicationsResponse, activityResponse] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getLeaseApplications({ page: 1, limit: 5 }),
        apiService.getRecentActivity({ limit: 5 })
      ]);

      if (statsResponse.status === 'success' && statsResponse.data) {
        // Map the backend response to our frontend state
        const backendData = statsResponse.data;
        setStats({
          totalShops: backendData.overview?.totalShops || 0,
          availableShops: backendData.shops?.available || 0,
          pendingApplications: backendData.applications?.submitted + backendData.applications?.underReview || 0,
          monthlyRevenue: backendData.revenue?.totalMonthlyRevenue || 0,
          occupancyRate: backendData.overview?.occupancyRate || 0,
          totalTenants: backendData.overview?.totalTenants || backendData.tenants?.totalTenants || 0
        });

        const paymentsTotals = backendData.tenants?.payments?.totals;
        if (paymentsTotals) {
          setTenantPaymentStats({
            totalCollected: paymentsTotals.totalCollected || 0,
            collectedThisMonth: paymentsTotals.collectedThisMonth || 0,
            paymentsCount: paymentsTotals.paymentsCount || 0,
            lastPaymentAt: paymentsTotals.lastPaymentAt ? new Date(paymentsTotals.lastPaymentAt).toISOString() : null
          });
        } else {
          setTenantPaymentStats({
            totalCollected: 0,
            collectedThisMonth: 0,
            paymentsCount: 0,
            lastPaymentAt: null
          });
        }

        setRecentTenantPayments(backendData.tenants?.payments?.recentPayments || []);
        setTopTenantsByPaid(backendData.tenants?.payments?.topTenantsByPaid || []);
      }

      if (applicationsResponse.status === 'success' && applicationsResponse.data?.applications) {
        setRecentApplications(applicationsResponse.data.applications);
      }

      if (activityResponse.status === 'success' && activityResponse.data?.activities) {
        setRecentActivity(activityResponse.data.activities);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Shops',
      value: stats.totalShops || 0,
      icon: <Building2 className="h-6 w-6" />,
      color: 'bg-blue-500',
      change: '+2 this month'
    },
    {
      title: 'Available Shops',
      value: stats.availableShops || 0,
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'bg-green-500',
      change: `${stats.occupancyRate || 0}% occupied`
    },
    {
      title: 'Pending Applications',
      value: stats.pendingApplications || 0,
      icon: <FileText className="h-6 w-6" />,
      color: 'bg-yellow-500',
      change: 'Need review'
    },
    {
      title: 'Rent Collected (This Month)',
      value: `$${(tenantPaymentStats.collectedThisMonth || 0).toLocaleString()}`,
      icon: <DollarSign className="h-6 w-6" />,
      color: 'bg-primary',
      change: `${tenantPaymentStats.paymentsCount || 0} payment${tenantPaymentStats.paymentsCount === 1 ? '' : 's'} recorded`
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (type: string, action: string) => {
    switch (type) {
      case 'application':
        return <FileText className="h-3 w-3 sm:h-4 sm:w-4" />;
      case 'shop':
        return <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />;
      case 'tenant':
        return <Users className="h-3 w-3 sm:h-4 sm:w-4" />;
      default:
        return <Clock className="h-3 w-3 sm:h-4 sm:w-4" />;
    }
  };

  const getActivityColor = (type: string, action: string) => {
    switch (type) {
      case 'application':
        return action === 'submitted' ? 'bg-green-500' : 'bg-blue-500';
      case 'shop':
        return action === 'rented' ? 'bg-green-500' : 
               action === 'reserved' ? 'bg-yellow-500' : 'bg-blue-500';
      case 'tenant':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return time.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-500 mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadDashboardData}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">Welcome back to Gandi Mall management system</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`p-2 sm:p-3 rounded-lg ${stat.color} text-white`}>
                  <div className="h-4 w-4 sm:h-6 sm:w-6">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{stat.title}</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">{stat.change}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Quick Actions</h3>
            <div className="space-y-2 sm:space-y-3">
              <Link
                to="/admin/shops"
                className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-light-gray rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
              >
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <span>Manage Shops</span>
              </Link>
              <Link
                to="/admin/applications"
                className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-light-gray rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
              >
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <span>Review Applications</span>
              </Link>
              <button className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-light-gray rounded-lg hover:bg-gray-200 transition-colors w-full text-left text-sm sm:text-base">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                <span>Generate Reports</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Recent Activity</h3>
            <div className="space-y-2 sm:space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={activity.id || index} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${getActivityColor(activity.type, activity.action)} rounded-full flex-shrink-0`}></div>
                    <span className="text-gray-600 flex-1 min-w-0">{activity.description}</span>
                    <span className="text-gray-400 text-xs">{formatTimeAgo(activity.timestamp)}</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <div className="text-gray-400 mb-2">
                    <Clock className="h-8 w-8 mx-auto" />
                  </div>
                  <p className="text-gray-500 text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tenant Payment Snapshot */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Rent Payments</h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Total collected: <span className="font-medium">${(tenantPaymentStats.totalCollected || 0).toLocaleString()}</span>
                    {tenantPaymentStats.lastPaymentAt ? (
                      <span className="ml-2 text-gray-500">
                        • last: {new Date(tenantPaymentStats.lastPaymentAt).toLocaleDateString()}
                      </span>
                    ) : null}
                  </p>
                </div>
                <Link
                  to="/admin/tenants"
                  className="text-primary hover:text-primary-dark font-medium text-xs sm:text-sm"
                >
                  Manage Tenants
                </Link>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-light-gray">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tenant
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentTenantPayments.length > 0 ? recentTenantPayments.map((row, index) => (
                    <tr key={`${row.tenantId}-${row.payment?._id || index}`} className="hover:bg-light-gray transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm font-medium text-gray-900">{row.tenantName || 'N/A'}</div>
                        <div className="text-xs sm:text-sm text-gray-500">{row.companyName || ''}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {row.payment?.period || '-'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        ${Number(row.payment?.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                        {row.payment?.paidAt ? new Date(row.payment.paidAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td className="px-3 sm:px-6 py-6 text-sm text-gray-500" colSpan={4}>
                        No payments recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Tenants by Total Paid</h3>
              <p className="text-xs sm:text-sm text-gray-600">Based on all recorded payments</p>
            </div>
            <div className="p-4 sm:p-6">
              {topTenantsByPaid.length > 0 ? (
                <div className="space-y-3">
                  {topTenantsByPaid.map((t, idx) => (
                    <div key={t._id || idx} className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{t.tenantName || 'N/A'}</div>
                        <div className="text-xs text-gray-500 truncate">{t.companyName || ''}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">${Number(t.totalPaid || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{t.paymentsCount || 0} payments</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No payments recorded yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Applications */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Applications</h3>
              <Link
                to="/admin/applications"
                className="text-primary hover:text-primary-dark font-medium text-xs sm:text-sm"
              >
                View All
              </Link>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-light-gray">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shop
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Type
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentApplications.map((app, index) => (
                  <tr key={app.id || app._id || `app-${index}`} className="hover:bg-light-gray transition-colors">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900">{app.Consent?.ApplicantName || 'N/A'}</div>
                        <div className="text-xs sm:text-sm text-gray-500">{app.Company?.EmailAddress || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {app.shopNumber || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {app.Company?.BusinessType || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {app.Company?.CompanyName || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getStatusColor(app.status)}`}>
                        {app.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                      {new Date(app.submittedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}